import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { POSShopSettings, ShopSettingsFormData } from '../types/admin';

export function usePOSShopSettings() {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['pos-shop-settings', company?.id],
    queryFn: async (): Promise<POSShopSettings | null> => {
      if (!company?.id) return null;

      const { data, error } = await supabase
        .from('pos_shop_settings')
        .select('*')
        .eq('company_id', company.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return data;
    },
    enabled: !!company?.id,
  });
}

export function useCreateShopSettings() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (data: ShopSettingsFormData) => {
      if (!company?.id) throw new Error('No company selected');

      const { data: settings, error } = await supabase
        .from('pos_shop_settings')
        .insert({
          company_id: company.id,
          ...data,
        })
        .select()
        .single();

      if (error) throw error;
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-shop-settings'] });
    },
  });
}

export function useUpdateShopSettings() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (data: Partial<ShopSettingsFormData>) => {
      if (!company?.id) throw new Error('No company selected');

      const { data: settings, error } = await supabase
        .from('pos_shop_settings')
        .update(data)
        .eq('company_id', company.id)
        .select()
        .single();

      if (error) throw error;
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pos-shop-settings'] });
    },
  });
}
